import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Calendar, Check, X, Clock, User, FileText, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const STATUS_BADGES = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { color: 'bg-green-100 text-green-800', icon: Check },
  rejected: { color: 'bg-red-100 text-red-800', icon: X },
  cancelled: { color: 'bg-gray-100 text-gray-800', icon: X }
};

const HOLIDAY_TYPE_LABELS = {
  H: 'Holiday',
  S: 'Sickness',
  CC: 'Child Care',
  M: 'Maternity',
  P: 'Paternity'
};

const HolidayApprovalManager = () => {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  useEffect(() => {
    fetchHolidays();
  }, [filter]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const params = filter === 'all' ? {} : { status: filter };
      
      const response = await axios.get(
        `${API_BASE_URL}/holidays/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );
      
      // Sort by date descending
      const sortedHolidays = (response.data || []).sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setHolidays(sortedHolidays);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to load holiday requests");
    } finally {
      setLoading(false);
    }
  };


  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (startDate === endDate) {
      return start.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    return `${start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })} - ${end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`;
  };


  const groupHolidaysByUser = (holidays) => {
    const grouped = {};
    holidays.forEach(holiday => {
      const userName = holiday.user_name || 'Unknown User';
      if (!grouped[userName]) {
        grouped[userName] = [];
      }
      grouped[userName].push(holiday);
    });
    return grouped;
  };

  const groupHolidaysIntoSeries = (holidays) => {
    // Sort holidays by date
    const sorted = [...holidays].sort((a, b) => new Date(a.date) - new Date(b.date));
    const series = [];
    let currentSeries = null;

    sorted.forEach((holiday, index) => {
      const holidayDate = new Date(holiday.date);
      
      if (!currentSeries || 
          holiday.status !== currentSeries.status ||
          holiday.holiday_type !== currentSeries.holiday_type) {
        // Start new series
        currentSeries = {
          id: `series-${Date.now()}-${index}`,
          holidays: [holiday],
          startDate: holiday.date,
          endDate: holiday.date,
          status: holiday.status,
          holiday_type: holiday.holiday_type,
          notes: holiday.notes,
          user_name: holiday.user_name,
          can_approve: holiday.can_approve
        };
        series.push(currentSeries);
      } else {
        // Check if this holiday is consecutive (allowing for weekends)
        const lastDate = new Date(currentSeries.endDate);
        const daysDiff = Math.floor((holidayDate - lastDate) / (1000 * 60 * 60 * 24));
        
        // If difference is 1 day or it's after a weekend (3 days), add to current series
        if (daysDiff === 1 || (daysDiff === 3 && lastDate.getDay() === 5)) {
          currentSeries.holidays.push(holiday);
          currentSeries.endDate = holiday.date;
          if (holiday.notes && !currentSeries.notes) {
            currentSeries.notes = holiday.notes;
          }
        } else {
          // Start new series
          currentSeries = {
            id: `series-${Date.now()}-${index}`,
            holidays: [holiday],
            startDate: holiday.date,
            endDate: holiday.date,
            status: holiday.status,
            holiday_type: holiday.holiday_type,
            notes: holiday.notes,
            user_name: holiday.user_name,
            can_approve: holiday.can_approve
          };
          series.push(currentSeries);
        }
      }
    });

    return series;
  };

  const handleApproveSeries = async (series) => {
    try {
      const token = localStorage.getItem("access_token");
      
      if (series.holidays.length === 1) {
        // Single holiday - use individual endpoint
        await axios.post(
          `${API_BASE_URL}/holidays/${series.holidays[0].id}/approve/`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Multiple holidays - use bulk endpoint
        const holidayIds = series.holidays.map(h => h.id);
        await axios.post(
          `${API_BASE_URL}/holidays/bulk_approve/`,
          { holiday_ids: holidayIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Approved ${series.holidays.length} holiday${series.holidays.length > 1 ? 's' : ''}`);
      fetchHolidays();
    } catch (error) {
      console.error("Error approving holiday series:", error);
      toast.error(error.response?.data?.error || "Failed to approve holidays");
    }
  };

  const handleRejectSeries = (series) => {
    setSelectedHoliday(series);
    setIsRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedHoliday) return;
    
    try {
      const token = localStorage.getItem("access_token");
      const holidays = selectedHoliday.holidays || [selectedHoliday];
      
      if (holidays.length === 1) {
        // Single holiday - use individual endpoint
        await axios.post(
          `${API_BASE_URL}/holidays/${holidays[0].id}/reject/`,
          { rejection_reason: rejectionReason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Multiple holidays - use bulk endpoint
        const holidayIds = holidays.map(h => h.id);
        await axios.post(
          `${API_BASE_URL}/holidays/bulk_reject/`,
          { 
            holiday_ids: holidayIds,
            rejection_reason: rejectionReason 
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Rejected ${holidays.length} holiday${holidays.length > 1 ? 's' : ''}`);
      setIsRejectDialogOpen(false);
      setSelectedHoliday(null);
      setRejectionReason('');
      fetchHolidays();
    } catch (error) {
      console.error("Error rejecting holidays:", error);
      toast.error(error.response?.data?.error || "Failed to reject holidays");
    }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Holiday Approvals</h2>
              <div className="flex gap-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading holiday requests...</div>
            ) : holidays.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No {filter !== 'all' ? filter : ''} holiday requests found
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupHolidaysByUser(holidays)).map(([userName, userHolidays]) => (
                  <div key={userName} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="h-5 w-5 text-gray-500" />
                      <h3 className="text-lg font-semibold">{userName}</h3>
                      <span className="text-sm text-gray-500">
                        ({userHolidays.length} request{userHolidays.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {groupHolidaysIntoSeries(userHolidays).map(series => (
                        <div key={series.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {formatDateRange(series.startDate, series.endDate)}
                                </span>
                                {series.holidays.length > 1 && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                    {series.holidays.length} days
                                  </span>
                                )}
                                <span className="text-sm text-gray-600">
                                  ({HOLIDAY_TYPE_LABELS[series.holiday_type] || series.holiday_type})
                                </span>
                                <StatusBadge status={series.status} />
                              </div>
                              {series.notes && (
                                <div className="flex items-start gap-1 mt-1">
                                  <FileText className="h-3 w-3 text-gray-400 mt-0.5" />
                                  <p className="text-sm text-gray-600">{series.notes}</p>
                                </div>
                              )}
                              {series.status === 'approved' && series.holidays[0].approved_by_name && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Approved by {series.holidays[0].approved_by_name} on {new Date(series.holidays[0].approved_at).toLocaleDateString()}
                                </p>
                              )}
                              {series.status === 'rejected' && (
                                <div className="mt-1">
                                  <p className="text-xs text-gray-500">
                                    Rejected by {series.holidays[0].approved_by_name} on {new Date(series.holidays[0].approved_at).toLocaleDateString()}
                                  </p>
                                  {series.holidays[0].rejection_reason && (
                                    <div className="flex items-start gap-1 mt-1">
                                      <AlertCircle className="h-3 w-3 text-red-500 mt-0.5" />
                                      <p className="text-xs text-red-600">{series.holidays[0].rejection_reason}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {series.status === 'pending' && series.can_approve && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveSeries(series)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve {series.holidays.length > 1 ? 'All' : ''}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectSeries(series)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Reject {series.holidays.length > 1 ? 'All' : ''}
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Holiday Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Rejecting holiday request for {selectedHoliday?.user_name} 
                {selectedHoliday?.holidays ? (
                  <span className="font-medium">
                    {' '}({selectedHoliday.holidays.length} days: {formatDateRange(selectedHoliday.startDate, selectedHoliday.endDate)})
                  </span>
                ) : (
                  <span> on {selectedHoliday && new Date(selectedHoliday.date).toLocaleDateString()}</span>
                )}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                rows="3"
                placeholder="Please provide a reason for rejection..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsRejectDialogOpen(false);
              setRejectionReason('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRejectConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Holiday{selectedHoliday?.holidays && selectedHoliday.holidays.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayApprovalManager;